import type {
	NavigatorWithConnection,
	ShakaPlayer,
	ShakaPlayerConfiguration,
	ShakaPlayerWithMeta
} from '~/composables/useHlsPlayer/types';

import {
	getSavedBandwidthEstimate,
	isSessionProbeCompleted,
	loadShaka,
	markSessionProbeCompleted,
	onBandwidthImprovement,
	onLiveBandwidth,
	saveBandwidthEstimateFromProbe,
	startBandwidthProbing,
	stopBandwidthProbingIfIdle
} from '~/composables/useHlsPlayer/shakaLoader';
import { resolveFirstSegmentUrl } from '~/composables/useVideoUrls';

import { isIOSDevice } from '~/components/PropertyReelsFeed/useIOSSharedVideo';

const BUFFER_BEHIND = 5;
const REBUFFER_GOAL = 2;
// Conservative fallback when no bandwidth history exists. Starting at 2 Mbps
// lets Shaka pick a lower quality initially and upgrade quickly on good networks,
// avoiding a 30s+ hang on unknown slow connections (e.g. first visit on slow 4G).
const FALLBACK_BANDWIDTH_ESTIMATE = 2_000_000; // 2 Mbps
const STALL_THRESHOLD = 3;

export interface CreatePlayerOptions {
	isPreload?: boolean;
	config?: ShakaPlayerConfiguration;
	startTime?: number;
}

function buildPlayerConfig(isPreload: boolean): ShakaPlayerConfiguration {
	// Priority: navigator.connection slow hint > saved history (capped) > fallback.
	//
	// If the current connection is reported as 2G/3G, always use the connection
	// hint — even if there is a fast saved estimate from a previous WiFi session.
	// This handles the common "was on WiFi, now on weak 4G" scenario without
	// needing a probe round-trip. DevTools throttle does NOT change effectiveType,
	// so this only activates on real slow devices.
	const MAX_SEED_BPS = 6_000_000;
	const defaultBandwidthEstimate = (() => {
		if (import.meta.client) {
			const nav = navigator as NavigatorWithConnection;
			const type = nav.connection?.effectiveType;
			// Connection type overrides saved history on genuinely slow networks
			if (type === 'slow-2g') return 100_000;
			if (type === '2g') return 250_000;
			if (type === '3g') return 600_000;
		}

		const saved = getSavedBandwidthEstimate();
		if (saved !== null) return Math.min(saved, MAX_SEED_BPS);

		return FALLBACK_BANDWIDTH_ESTIMATE; // 2 Mbps for unknown/4g
	})();

	const config: ShakaPlayerConfiguration = {
		streaming: {
			bufferingGoal: isPreload ? 3 : 10,
			rebufferingGoal: REBUFFER_GOAL,
			bufferBehind: BUFFER_BEHIND,
			stallEnabled: true,
			stallThreshold: STALL_THRESHOLD,
			retryParameters: {
				maxAttempts: 2,
				baseDelay: 1000,
				backoffFactor: 2,
				// Shaka's timeout now properly cancels body downloads via readBodyChunked +
				// AbortableOperation — this is the safety net for completely unresponsive CDN.
				// Normal slow downloads complete naturally; ABR downgrades on the next segment.
				timeout: 30000
			}
		},
		abr: {
			enabled: true,
			defaultBandwidthEstimate,
			switchInterval: 2,
			bandwidthUpgradeTarget: 0.85,
			bandwidthDowngradeTarget: 0.7
		}
	};

	// Only on iOS: use Safari's native HLS (Shaka 4.16.13+)
	// This avoids MSE conflicts and reduces JS overhead
	if (isIOSDevice) {
		config.streaming!.preferNativeHls = true;
		// Disable ABR on iOS since native player handles it
		config.abr!.enabled = false;
	}

	return config;
}

export async function createPlayer(
	videoUrl: string,
	videoElement: HTMLVideoElement,
	options: CreatePlayerOptions = {}
): Promise<ShakaPlayer> {
	const isPreload = options.isPreload ?? false;

	const existing = (videoElement as HTMLVideoElement & { shaka?: ShakaPlayer })
		.shaka;
	if (existing) {
		await existing.destroy();
	}

	const shaka = await loadShaka();

	if (!shaka.Player.isBrowserSupported()) {
		throw new Error('Browser not supported');
	}

	// Cold-start probe: runs once per session even with saved history so the
	// initial quality reflects the CURRENT network (e.g. was on WiFi, now on 3G).
	// Side effect: pre-warms the manifest + variant playlist in CacheStorage.
	const connType = import.meta.client
		? (navigator as NavigatorWithConnection).connection?.effectiveType
		: undefined;
	const connectionIsSlow =
		connType === 'slow-2g' || connType === '2g' || connType === '3g';
	const needsProbe =
		!isPreload &&
		!isIOSDevice &&
		videoUrl.includes('.m3u8') &&
		(!isSessionProbeCompleted() ||
			getSavedBandwidthEstimate() === null ||
			connectionIsSlow);

	const coldProbeAbort = needsProbe ? new AbortController() : null;

	if (needsProbe && coldProbeAbort) {
		try {
			await Promise.race([
				(async () => {
					const segUrl = await resolveFirstSegmentUrl(
						videoUrl,
						coldProbeAbort.signal
					);
					if (!segUrl || coldProbeAbort.signal.aborted) return;
					await saveBandwidthEstimateFromProbe(segUrl, coldProbeAbort.signal);
				})(),
				new Promise<void>(resolve => setTimeout(resolve, 3000))
			]);
		} catch (err) {
			// Non-critical: falls back to navigator.connection hint or 2 Mbps default
			if ((err as Error)?.name !== 'AbortError') {
				console.error('[useSharedVideoPlayer] cold-start probe failed', {
					message: (err as Error)?.message,
					stack: (err as Error)?.stack
				});
			}
		} finally {
			markSessionProbeCompleted();
		}
	}

	const player = new shaka.Player() as ShakaPlayer;
	const config = options.config ?? buildPlayerConfig(isPreload);
	player.configure(config);

	let playerDestroyed = false;

	const unsubscribeProbe = onBandwidthImprovement(bps => {
		if (playerDestroyed) return;
		try {
			player.configure({ abr: { defaultBandwidthEstimate: bps } });
			if (bps >= 3_500_000) {
				player.configure({ abr: { restrictions: {} } });
			}
		} catch {
			// Ignore config errors on destroyed player
		}
	});

	const unsubscribeLive = onLiveBandwidth(bps => {
		if (playerDestroyed) return;
		try {
			if (bps < 3_500_000) {
				player.configure({
					abr: {
						defaultBandwidthEstimate: bps,
						restrictions: { maxBandwidth: Math.round(bps * 1.5) }
					}
				});
			} else {
				player.configure({
					abr: { restrictions: {}, defaultBandwidthEstimate: bps }
				});
			}
		} catch {
			// Ignore config errors on destroyed player
		}
	});

	startBandwidthProbing();

	const cleanupOnError = () => {
		coldProbeAbort?.abort();
		unsubscribeProbe();
		unsubscribeLive();
		destroyPlayer(player).catch(() => {});
	};

	try {
		await player.attach(videoElement);
	} catch (err) {
		console.error('[useSharedVideoPlayer] player.attach() failed', {
			message: (err as Error)?.message,
			stack: (err as Error)?.stack
		});
		cleanupOnError();
		throw err;
	}

	try {
		await player.load(videoUrl, options.startTime);
	} catch (err) {
		console.error('[useSharedVideoPlayer] player.load() failed', {
			message: (err as Error)?.message,
			videoUrl,
			stack: (err as Error)?.stack
		});
		cleanupOnError();
		throw err;
	}

	(player as ShakaPlayerWithMeta).__unsubscribeProbe = () => {
		unsubscribeProbe();
		unsubscribeLive();
	};
	(player as ShakaPlayerWithMeta).__setDestroyed = () => {
		playerDestroyed = true;
		coldProbeAbort?.abort();
	};
	(videoElement as HTMLVideoElement & { shaka?: ShakaPlayer }).shaka = player;
	return player;
}

export async function destroyPlayer(
	player: ShakaPlayer | null,
	videoElement?: HTMLVideoElement | null
): Promise<void> {
	if (!player) return;

	// Unsubscribe FIRST — prevents callbacks from trying to configure a destroyed player
	(player as ShakaPlayerWithMeta).__setDestroyed?.();
	(player as ShakaPlayerWithMeta).__unsubscribeProbe?.();
	stopBandwidthProbingIfIdle();

	try {
		// Unload first to immediately cancel all in-flight network requests
		await player.unload();
	} catch {
		// Ignore unload errors
	}

	try {
		await player.destroy();
	} catch {
		// Ignore destroy errors
	}

	// Shaka Player instances contain non-cloneable closures; removing the ref
	// avoids DataCloneError on iOS when the element is transferred/serialised.
	if (videoElement) {
		try {
			delete (videoElement as HTMLVideoElement & { shaka?: ShakaPlayer }).shaka;
		} catch {
			// Ignore deletion errors
		}
	}
}

export { clearVideoCache } from '~/composables/useHlsPlayer/shakaLoader';
